import { useEffect, useMemo, useRef, useState } from 'react';

const PREVIEW_WIDTH = 600;
const PREVIEW_HEIGHT = 800;

function isRenderablePreviewHtml(html: string | null | undefined): html is string {
  if (!html) return false;
  const trimmed = html.trim();
  return trimmed.length >= 40 && /<[a-z][\s>]/i.test(trimmed);
}

function buildSrcDoc(html: string): string {
  const trimmed = html.trim();
  if (/<html[\s>]/i.test(trimmed)) {
    return trimmed.replace(
      /<head([^>]*)>/i,
      (m) =>
        `${m}<meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><base target="_blank"/><style>html,body{margin:0;padding:0;background:#fff;}</style>`,
    );
  }
  return `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><style>html,body{margin:0;padding:0;background:#fff;}</style></head><body>${trimmed}</body></html>`;
}

export function DraftCardPreview({
  html,
  subject,
}: {
  html: string | null | undefined;
  subject: string | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.45);
  const [renderFailed, setRenderFailed] = useState(false);

  const canPreview = isRenderablePreviewHtml(html) && !renderFailed;
  const srcDoc = useMemo(() => (canPreview ? buildSrcDoc(html) : ''), [canPreview, html]);

  useEffect(() => {
    setRenderFailed(false);
  }, [html]);

  useEffect(() => {
    const el = sheetRef.current;
    if (!el || !canPreview) return;

    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / PREVIEW_WIDTH);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [canPreview]);

  if (!canPreview) {
    return (
      <div className="bg-muted/70 flex aspect-[3/2] items-start justify-center overflow-hidden px-6 pt-5">
        <div className="border-border/70 bg-card flex h-full w-[68%] max-w-[220px] flex-col items-center justify-center gap-1 rounded-sm border px-4 text-center shadow-sm">
          <span className="text-muted-foreground text-xs font-medium">Email preview</span>
          {subject ? (
            <span className="text-muted-foreground/80 line-clamp-2 text-[11px]">{subject}</span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="bg-muted/70 relative aspect-[3/2] overflow-hidden px-6 pt-5"
    >
      <div
        ref={sheetRef}
        className="border-border/70 relative mx-auto h-full w-[68%] max-w-[220px] overflow-hidden rounded-sm border bg-white shadow-sm"
      >
        <iframe
          title={subject ? `Preview: ${subject}` : 'Email preview'}
          srcDoc={srcDoc}
          className="pointer-events-none absolute left-0 top-0 border-0 bg-white"
          style={{
            width: PREVIEW_WIDTH,
            height: PREVIEW_HEIGHT,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
          onError={() => setRenderFailed(true)}
        />
      </div>
    </div>
  );
}
